'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Image from 'next/image';

import { useAuth } from '@/contexts/AuthContext';
import {
  MessageSquare, Calculator, Users, ChevronLeft, ChevronRight,
  Database, Bell, Calendar, Shield, LogOut, User, LogIn, Building2, Zap,
} from 'lucide-react';



const UNREAD_COUNT_KEY = 'logistis_news_unread_count';

const ROLE_LABELS: Record<string, string> = {
  free:       'Δωρεάν',
  premium:    'Premium',
  business:   'Business',
  superadmin: 'Admin',
};

const ROLE_BADGE_CLASS: Record<string, string> = {
  free:       'bg-gray-100 text-gray-600',
  premium:    'bg-blue-100 text-blue-800',
  business:   'bg-purple-100 text-purple-800',
  superadmin: 'bg-amber-100 text-amber-800',
};

// Which roles can see each nav item
const ALL_NAV = [
  { href: '/ai-chat',           label: 'Συνομιλία AI',    icon: MessageSquare, roles: ['free','premium','business','superadmin'], showBadge: false },
  { href: '/news',              label: 'Νέα',             icon: Bell,          roles: ['free','premium','business','superadmin'], showBadge: true  },
  { href: '/deadlines',         label: 'Ημερολόγιο',      icon: Calendar,      roles: ['free','premium','business','superadmin'], showBadge: false },
  { href: '/tax-simulator',     label: 'Προσομοιωτής',    icon: Calculator,    roles: ['premium','business','superadmin'],        showBadge: false },
  { href: '/client-management', label: 'Πελάτες',         icon: Users,         roles: ['business','superadmin'],                  showBadge: false },
  { href: '/gemi',              label: 'Αναζήτηση Επιχ.', icon: Building2,     roles: ['premium','business','superadmin'],        showBadge: false },
  { href: '/database',          label: 'Βάση Νόμων',      icon: Database,      roles: ['business','superadmin'],                  showBadge: false },
  { href: '/admin',             label: 'Admin',           icon: Shield,        roles: ['superadmin'],                             showBadge: false },
  { href: '/pricing',           label: 'Αναβάθμιση',      icon: Zap,           roles: ['free', 'superadmin'],                     showBadge: false },
];

export default function Sidebar() {
  const pathname  = usePathname();
  const router    = useRouter();
  const { user, profile, role, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  // Auto-collapse on small height screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerHeight < 600) setCollapsed(true);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setCollapsed(true);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [newsUnreadCount, setNewsUnreadCount] = useState(0);

  useEffect(() => {
    const count = parseInt(localStorage.getItem(UNREAD_COUNT_KEY) || '0', 10) || 0;
    setNewsUnreadCount(count);
    const interval = setInterval(() => {
      setNewsUnreadCount(parseInt(localStorage.getItem(UNREAD_COUNT_KEY) || '0', 10) || 0);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (pathname === '/news') setNewsUnreadCount(0);
  }, [pathname]);

  const handleSignOut = async () => {
    await signOut();
    router.push('/auth/login');
  };

  // ── FIX: when role is null (loading), show only free items
  // Old buggy code showed ALL items when role was null
  const visibleNav = ALL_NAV.filter(item =>
    role ? item.roles.includes(role) : item.roles.includes('free')
  );

  return (
    <aside className={`flex flex-col bg-blue-950 text-white transition-all duration-300 ease-in-out shrink-0 h-screen ${collapsed ? 'w-16' : 'w-60'} hidden md:flex`}>
      {/* Logo */}
      <div id="tour-logo" className={`flex items-center gap-3 px-4 py-5 border-b border-blue-900 ${collapsed ? 'justify-center px-2' : ''}`}>
        <Image
          src="/logo-icon.png"
          alt="AskΦόρος"
          width={32}
          height={32}
          className="shrink-0 rounded-lg"
          priority
          unoptimized
        />
        {!collapsed && (
          <span className="font-semibold text-base tracking-tight whitespace-nowrap text-white">
            AskΦόρος
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto min-h-0">
        {visibleNav.map((item) => {
          const NavIcon = item.icon;
          const isActive = pathname === item.href || pathname?.startsWith(item.href + '/');
          const showBadge = item.showBadge && newsUnreadCount > 0;

          return (
            <Link
              key={item.href}
              href={item.href}
              title={collapsed ? item.label : undefined}
              id={
                item.href === '/news'              ? 'tour-nav-news' :
                item.href === '/deadlines'         ? 'tour-nav-deadlines' :
                item.href === '/tax-simulator'     ? 'tour-nav-simulator' :
                item.href === '/client-management' ? 'tour-nav-clients' :
                item.href === '/gemi'              ? 'tour-nav-gemi' :
                undefined
              }
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative ${
                isActive ? 'bg-blue-800 text-white' : 'text-blue-200 hover:bg-blue-900 hover:text-white'
              }`}
            >
              <span className="relative shrink-0">
                <NavIcon size={18} />
                {showBadge && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] bg-red-500 rounded-full flex items-center justify-center text-white text-[9px] font-bold px-0.5 border border-blue-950">
                    {newsUnreadCount > 99 ? '99+' : newsUnreadCount}
                  </span>
                )}
              </span>
              {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
              {!collapsed && showBadge && (
                <span className="ml-auto min-w-[20px] h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold px-1">
                  {newsUnreadCount > 99 ? '99+' : newsUnreadCount}
                </span>
              )}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="border-t border-blue-900 p-2 space-y-1 shrink-0">
        {user ? (
          <>
            {!collapsed && (
              <div className="px-3 py-2 rounded-lg bg-blue-900/50">
                <div className="flex items-center gap-2 mb-1">
                  <User size={13} className="text-blue-300 shrink-0" />
                  <span className="text-xs text-blue-200 truncate">{profile?.email || user.email}</span>
                </div>
                {role && (
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${ROLE_BADGE_CLASS[role] || 'bg-gray-100 text-gray-600'}`}>
                    {ROLE_LABELS[role] || role}
                  </span>
                )}
              </div>
            )}
            <button
              id="tour-replay-btn"
              onClick={() => (window as any).__askforosTour?.()}
              title={collapsed ? 'Ξενάγηση' : undefined}
              className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-blue-300 hover:bg-blue-900/50 hover:text-white text-xs transition-all group relative"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
              {!collapsed && <span>Ξενάγηση</span>}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                  Ξενάγηση
                </span>
              )}
            </button>
            <button
              onClick={handleSignOut}
              title={collapsed ? 'Αποσύνδεση' : undefined}
              className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-blue-300 hover:bg-blue-900 hover:text-white text-sm transition-all group relative"
            >
              <LogOut size={16} className="shrink-0" />
              {!collapsed && <span>Αποσύνδεση</span>}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                  Αποσύνδεση
                </span>
              )}
            </button>
          </>
        ) : (
          <Link
            href="/auth/login"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-blue-300 hover:bg-blue-900 hover:text-white text-sm transition-all"
          >
            <LogIn size={16} className="shrink-0" />
            {!collapsed && <span>Σύνδεση</span>}
          </Link>
        )}

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center w-full py-2 rounded-lg text-blue-300 hover:bg-blue-900 hover:text-white transition-all"
        >
          {collapsed ? <ChevronRight size={18} /> : (
            <span className="flex items-center gap-2 text-sm">
              <ChevronLeft size={18} />
              <span>Σύμπτυξη</span>
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}